/**
 * Four-state pillar status model + Next-Best-Action heuristic.
 *
 * Drives the redesigned Simple View of the Claim Tracker. Distinct from the
 * legacy "have"/"need" model used in Advanced View, the Master PDF, and
 * Public Share — both models coexist; this one is purely a UX rollup over
 * the same evidence + per-issue flags.
 *
 *   complete      — ≥1 evidence item attached AND user marked the pillar reviewed
 *   needsReview   — ≥1 evidence item attached but never marked reviewed
 *   missing       — 0 items AND the user has explicitly touched the pillar (any
 *                   View / Add click flips issue.pillarTouched[id] = true)
 *   notStarted    — 0 items, never touched (default for fresh conditions)
 *
 * Nexus waiver (BDD or FDC-within-1yr) auto-completes the nexus pillar so
 * the same waiver logic surfaces here as well.
 */
import { evidenceElements } from "./claimData";
import { getNexusCoverage } from "./riskEngine";

export const PILLAR_STATE = {
  complete: "complete",
  needsReview: "needsReview",
  missing: "missing",
  notStarted: "notStarted",
};

export const PILLAR_LABEL = {
  complete: "Complete",
  needsReview: "Needs Review",
  missing: "Missing",
  notStarted: "Not Started",
};

export const PILLAR_CLASS = {
  complete: "pill-state pill-state--complete",
  needsReview: "pill-state pill-state--review",
  missing: "pill-state pill-state--missing",
  notStarted: "pill-state pill-state--notstarted",
};

export function pillarStateForIssue(issue, elementId, evidence = [], ctx = {}) {
  // Nexus pillar: theory-aware coverage (waiver, in-service evidence for
  // Direct/BDD/presumptive theories, or explicit nexus evidence)
  if (elementId === "nexus") {
    const cov = getNexusCoverage(issue, evidence, ctx);
    if (cov.covered) return PILLAR_STATE.complete;
  }
  const attached = evidence.filter((ev) => ev.issueIds?.includes(issue.id) && ev.element === elementId);
  const reviewed = !!issue.pillarReviewed?.[elementId];
  const touched = !!issue.pillarTouched?.[elementId];
  if (attached.length > 0) {
    return reviewed ? PILLAR_STATE.complete : PILLAR_STATE.needsReview;
  }
  return touched ? PILLAR_STATE.missing : PILLAR_STATE.notStarted;
}

/**
 * Roll one issue's four pillar states into a single "headline" state for the
 * left-side condition list. Conservative priority: any missing > needsReview >
 * notStarted > all complete.
 */
export function rollupIssueState(issue, evidence = [], ctx = {}) {
  const states = evidenceElements.map((el) => pillarStateForIssue(issue, el.id, evidence, ctx));
  if (states.some((s) => s === PILLAR_STATE.missing)) return PILLAR_STATE.missing;
  if (states.some((s) => s === PILLAR_STATE.needsReview)) return PILLAR_STATE.needsReview;
  if (states.some((s) => s === PILLAR_STATE.notStarted)) return PILLAR_STATE.notStarted;
  return PILLAR_STATE.complete;
}

/**
 * Compute a single "Next Best Action" recommendation. Picks the highest-leverage
 * gap across all conditions in the workspace, weighted by:
 *   - missing > notStarted > needsReview
 *   - High > Medium > Low priority condition
 *   - Pillar weight: service > current > nexus > severity (in-service event is
 *     the rarest to recover late, so we surface it first)
 *
 * Returns { issueId, issueName, elementId, elementLabel, state, headline, copy } or
 * null if everything is complete.
 */
const STATE_WEIGHT = {
  [PILLAR_STATE.missing]: 3,
  [PILLAR_STATE.notStarted]: 2,
  [PILLAR_STATE.needsReview]: 1,
  [PILLAR_STATE.complete]: 0,
};
const PILLAR_WEIGHT = { service: 4, current: 3, nexus: 2, severity: 1 };
const PRIORITY_WEIGHT = { High: 3, Medium: 2, Low: 1 };

const ACTION_COPY = {
  service: {
    missing: (n) => `Upload an STR or buddy statement linking your ${n} to a specific service event.`,
    notStarted: (n) => `Start collecting service records for your ${n} claim — STRs, deployment logs, or unit buddy statements.`,
    needsReview: (n) => `Review the service-event evidence on your ${n} claim and mark it complete when it looks right.`,
  },
  current: {
    missing: (n) => `Add a recent diagnosis or treatment record for your ${n} claim.`,
    notStarted: (n) => `Upload a current diagnosis (VA or civilian) for your ${n} claim — this anchors the whole packet.`,
    needsReview: (n) => `Review the current-diagnosis evidence on ${n} and mark it complete.`,
  },
  nexus: {
    missing: (n) => `Get a nexus letter or document the continuity-of-symptoms link for your ${n} claim.`,
    notStarted: (n) => `Plan the nexus link for ${n} — IMO letter, secondary chain, or presumptive pathway.`,
    needsReview: (n) => `Review the nexus/link evidence on ${n} and mark it complete.`,
  },
  severity: {
    missing: (n) => `Log a recent symptom flare-up or functional-impact note for your ${n} claim.`,
    notStarted: (n) => `Add a severity/function note for ${n} — symptom logs, work impact, or daily limitations.`,
    needsReview: (n) => `Review the severity/function evidence on ${n} and mark it complete.`,
  },
};

/**
 * Body-system overrides for the NBA copy. When a condition belongs to a
 * given system, we replace the generic prompt with one that names the kinds
 * of evidence raters actually expect for THAT body system. This prevents
 * leakage like "ROM testing" guidance appearing on a mental-health claim.
 *
 * Only systems with materially different evidence patterns are overridden.
 * Anything not listed falls back to the generic ACTION_COPY above.
 */
const SYSTEM_ACTION_OVERRIDES = {
  mental: {
    service: {
      missing: (n) => `Document the in-service stressor or onset for your ${n} claim. STRs, mental-health sick-call slips, deployment / mobilization orders, or a buddy statement describing when the symptoms first appeared.`,
      notStarted: (n) => `Begin documenting the in-service stressor or onset for ${n}. Pull STRs, mental-health treatment notes, and any incident, MST, or harassment reports.`,
      needsReview: (n) => `Re-read the in-service stressor evidence for ${n} and confirm it matches the personal statement and any buddy statements.`,
    },
    current: {
      missing: (n) => `Add a current mental-health diagnosis for your ${n} claim — VA or civilian provider note that names the disorder.`,
      notStarted: (n) => `Upload a current mental-health diagnosis for ${n}: psychiatrist / psychologist / LCSW note, or VA mental-health consult.`,
      needsReview: (n) => `Review the current diagnosis on ${n} (provider, date, exact disorder language) and mark it complete.`,
    },
    nexus: {
      missing: (n) => `Document the link for ${n}: continuity of symptoms since service, an IMO opinion ("at least as likely as not"), or a clear secondary chain.`,
      notStarted: (n) => `Plan the nexus link for ${n} — for direct service connection, build continuity-of-symptoms from discharge to present.`,
      needsReview: (n) => `Re-read the nexus / continuity evidence on ${n} and confirm the dates and language are tight.`,
    },
    severity: {
      missing: (n) => `Add severity evidence for ${n}: occupational and social impairment, sleep impairment, medication history, panic / anxiety episodes, suicidal ideation if present, and a functional-impact paragraph.`,
      notStarted: (n) => `Start the severity record for ${n}: therapy frequency, medication list, missed work, isolation, panic episodes, sleep diary.`,
      needsReview: (n) => `Review the severity evidence on ${n} against the § 4.130 General Rating Formula and confirm every level descriptor is supported.`,
    },
  },
  musculoskeletal: {
    severity: {
      missing: (n) => `Log severity for ${n}: range-of-motion limits, flare-up frequency / triggers / duration, functional loss (sitting, standing, lifting), and missed-work / activity-loss evidence.`,
      notStarted: (n) => `Start a severity record for ${n} — ROM measurements, PT notes, flare-up log, and impact on lifting / sitting / standing.`,
    },
  },
  hearing: {
    current: {
      missing: (n) => `Add an audiogram for ${n} (puretone thresholds + Maryland CNC speech discrimination) — VA exam or qualified private audiologist.`,
      notStarted: (n) => `Upload an audiogram for ${n} — puretone thresholds + Maryland CNC speech discrimination.`,
    },
    service: {
      missing: (n) => `Document in-service noise exposure for ${n}: MOS / billet noise-exposure tables, hazardous-duty orders, hearing-conservation enrollment records, or a buddy statement on the noise environment.`,
      notStarted: (n) => `Start documenting in-service noise exposure for ${n}: MOS noise-exposure tables, hazardous-duty orders, hearing-conservation enrollment, or buddy statements.`,
    },
  },
  respiratory: {
    severity: {
      missing: (n) => `Log severity for ${n}: PFT results (FEV-1, FVC), exacerbation count, oxygen / inhaler use, and any cor pulmonale findings.`,
    },
  },
  neurological: {
    severity: {
      missing: (n) => `Log severity for ${n}: prostrating-attack frequency (key threshold), neurological deficits, medication regimen, and missed-work days.`,
    },
  },
};

function actionCopyFor(issue, elementId, state) {
  const system = (issue?.system || "").toLowerCase();
  const override = SYSTEM_ACTION_OVERRIDES[system]?.[elementId]?.[state];
  if (override) return override;
  return ACTION_COPY[elementId]?.[state];
}

/**
 * Pick the single next best action for the veteran.
 *
 * @param {Array}  issues    All issues in scope.
 * @param {Array}  evidence  All evidence in scope.
 * @param {Object} ctx       { workspace, master526ez } for waiver detection.
 * @param {Object} opts
 * @param {string} opts.restrictToIssueId  Limit scoring to a single issue id.
 *                                          When set, the result is guaranteed
 *                                          to be condition-specific — no
 *                                          template leakage from siblings.
 */
export function getNextBestAction(issues = [], evidence = [], ctx = {}, opts = {}) {
  const scoped = opts.restrictToIssueId
    ? issues.filter((i) => i.id === opts.restrictToIssueId)
    : issues;
  let best = null;
  let bestScore = -1;
  for (const issue of scoped) {
    const pri = PRIORITY_WEIGHT[issue.priority] ?? PRIORITY_WEIGHT.Medium;
    for (const el of evidenceElements) {
      const state = pillarStateForIssue(issue, el.id, evidence, ctx);
      if (state === PILLAR_STATE.complete) continue;
      const score = STATE_WEIGHT[state] * 100 + PILLAR_WEIGHT[el.id] * 10 + pri;
      if (score > bestScore) {
        bestScore = score;
        best = { issue, el, state };
      }
    }
  }
  if (!best) return null;
  const name = best.issue.claimedAs || "this condition";
  const copyFn = actionCopyFor(best.issue, best.el.id, best.state)
    || (() => `Take a look at ${best.el.label.toLowerCase()} for ${name}.`);
  return {
    issueId: best.issue.id,
    issueName: name,
    elementId: best.el.id,
    elementLabel: best.el.label,
    state: best.state,
    headline: `${best.el.label} on ${name}`,
    copy: copyFn(name),
  };
}
